import { gsap } from 'gsap'
import { deepMerge } from 'js/utils/Utils'
import Common from 'js/_core/Common'

const defaults = {
  $names: {
    modal: '[data-modal="teams-modal"]',
    dlg: '[data-dlg="teams-dlg"]',
    close: '[data-modal="teams-modal"] [data-close]',
  },
  apiBase: 'https://gnmd.com/?rest_route=/wp/v2/staff',
  apiBaseEN: 'https://gnmd.com/en/?rest_route=/wp/v2/staff',
}

export default class TeamsModal {
  $contentRoot = document
  $modal = document.getElementById('modal')
  $dlg = document.getElementById('dlg')
  $html = document.documentElement
  $body = document.body
  $closes = []

  lang = 'ja'

  cache = new Map()
  lastActive = null
  isFixBody = true
  constructor(options = {}) {
    deepMerge(this, defaults, options)
  }

  init() {
    this.checkLang()
    this._set$()
    this._addEventListeners()
    if (this.$modal) {
      gsap.set(this.$modal, { opacity: 0, display: 'none' })
    }
  }

  destroy() {
    this._removeEventListeners()
  }

  checkLang() {
    const htmlLang = document.documentElement.getAttribute('lang')
    if (htmlLang !== 'ja') {
      this.lang = 'en'
      this.apiBase = this.apiBaseEN
    }
  }

  _set$() {
    this.$modal = this.$contentRoot.querySelector(this.$names.modal)
    this.$dlg = this.$contentRoot.querySelector(this.$names.dlg)
    this.$closes = this.$contentRoot.querySelectorAll(this.$names.close)
  }

  _addEventListeners() {
    // this.$modal.addEventListener('click', (e) => {
    //   if (e.target.matches('[data-close]')) this.closeModal()
    // })

    this.$closes.forEach(($btn) => {
      $btn.addEventListener('click', () => {
        this.closeModal()
      })
    })

    document.addEventListener('keydown', this._onKeydown)
    document.addEventListener('click', this._onClick)
  }

  _removeEventListeners() {
    document.removeEventListener('keydown', this._onKeydown)
    document.removeEventListener('click', this._onClick)
  }

  _onClick = (e) => {
    const btn = e.target.closest('[data-teams-id]')
    if (!btn) return
    const id = btn.getAttribute('data-teams-id')

    this.lastActive = document.activeElement
    this.openModal()
    this.$dlg.innerHTML = '<div class="loading">Loading…</div>'

    try {
      this.getDetail(id).then((data) => {
        this.$dlg.innerHTML = this.renderDetail(data)

        const $modalVisual = this.$dlg.querySelector('.teams_modal_visual')

        requestAnimationFrame(() => {
          if ($modalVisual) {
            gsap.to($modalVisual, {
              opacity: 1,
              duration: 0.3,
              delay: 0.1,
            })
          }
        })
      })
    } catch (err) {
      console.error(err)
      this.$dlg.innerHTML = '<p class="loading">読み込みに失敗しました。</p>'
    }
  }

  _onKeydown = (e) => {
    if (e.key === 'Escape' && this.$modal.hasAttribute('open')) this.closeModal()
  }

  // --- Modal 基本 ---
  openModal() {
    this.$modal.setAttribute('open', '')
    this.$modal.style.display = 'block'

    if (this.isFixBody) {
      this.scrollTop = this.$html.scrollTop || this.$body.scrollTop || window.scrollY
      Common.isScrollingFixed = true
    }

    if (this.isFixBody) {
      this.scrollTop = this.$html.scrollTop || this.$body.scrollTop || window.scrollY
      requestAnimationFrame(() => {
        this.$body.style.position = 'fixed'
        this.$body.style.top = `-${this.scrollTop}px`
        this.$body.style.left = 0
        this.$body.style.overflow = 'clip'

        this.$html.scrollTop = 0
        this.$body.scrollTop = 0
      })
    }

    if (this.$modal) {
      gsap.killTweensOf(this.$modal)
      gsap.to(this.$modal, {
        opacity: 1,
      })
    }
  }

  closeModal() {
    this.$modal.removeAttribute('open')
    document.body.style.overflow = ''

    if (this.$modal) {
      gsap.killTweensOf(this.$modal)
      gsap.to(this.$modal, {
        opacity: 0,
        onComplete: () => {
          if (this.isFixBody) {
            requestAnimationFrame(() => {
              this.$body.style.position = ''
              this.$body.style.top = ''
              this.$body.style.left = ''
              this.$body.style.overflow = ''
              this.$html.scrollTop = this.$body.scrollTop = this.scrollTop
              Common.isScrollingFixed = false
            })
          }
          this.$modal.style.display = 'none'
          if (this.lastActive) this.lastActive.focus()
        },
      })
    }
  }

  // --- 詳細データ取得（ACF整形 + thumbnail_sizes を REST で返す実装がWP側にある前提） ---
  async getDetail(id) {
    if (this.cache.has(id)) return this.cache.get(id)
    const url = `${this.apiBase}/${id}&_fields=id,title,content,acf,thumbnail_url,thumbnail_sizes`
    const res = await fetch(url, { credentials: 'omit' })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    this.cache.set(id, data)
    return data
  }

  // --- 描画 ---
  renderDetail(d) {
    const name = d.title?.rendered ?? ''
    const role = d.acf?.position ?? ''
    const bio = d.acf?.bio ?? d.content?.rendered ?? ''
    const links = Array.isArray(d.acf?.links) ? d.acf.links : []
    const t = d.thumbnail_sizes || {}
    // モーダルは大きめを優先
    const modalUrl = t.large?.url || t.full?.url || t.medium_large?.url || t.medium?.url || d.thumbnail_url || ''
    const modalSrcset = t._srcset || ''
    const modalSizes = t._sizes || '(max-width: 960px) 92vw, 720px'

    if (this.lang === 'en') {
      return `
      <div>
        <div class="teams_modal_visual" style="opacity: 0">
            <div class="teams_modal_visual_img">
                ${modalUrl ? `<img src="${modalUrl}" ${modalSrcset ? `srcset="${modalSrcset}" sizes="${modalSizes}"` : ''} alt="">` : `<div style="background:#f3f3f3; aspect-ratio:1/1;"></div>`}
              </div>
        </div>
        <div class="teams_modal_head">
            ${role ? `<div class="teams_modal_role | f-helvetica-now-bld">${this.escapeHTML(role)}</div>` : ''}
            <div class="teams_modal_name | f-helvetica-now-bld">${name}</div>
        </div>
        <div class="teams_modal_body">
            <div class="teams_modal_content | f-helvetica-now-med">${bio}</div>
        </div>
        ${
          links.length
            ? `
        <div class="teams_modal_foot">
            <div class="teams_modal_foot_label | f-helvetica-now-bld">Links</div>
            <ul class="teams_modal_foot_list">
                ${links
                  .map(
                    (s) => `<li class="teams_modal_foot_list_i">
                    <a href="${s.url}" target="_blank" rel="noopener noferrer" class="teams_modal_foot_list_link | f-helvetica-now-bld">${this.escapeHTML(s.label || s.url)}</a>
                </li>`
                  )
                  .join('')}
            </ul>
        </div>`
            : ''
        }
      </div>
    `
    } else {
      return `
      <div>
        <div class="teams_modal_visual" style="opacity: 0">
            <div class="teams_modal_visual_img">
                ${modalUrl ? `<img src="${modalUrl}" ${modalSrcset ? `srcset="${modalSrcset}" sizes="${modalSizes}"` : ''} alt="">` : `<div style="background:#f3f3f3; aspect-ratio:1/1;"></div>`}
              </div>
        </div>
        <div class="teams_modal_head">
            ${role ? `<div class="teams_modal_role | f-helvetica-now-bld">${this.escapeHTML(role)}</div>` : ''}
            <div class="teams_modal_name | f-helvetica-now-bld">${name}</div>
        </div>
        <div class="teams_modal_body">
            <div class="teams_modal_content | f-a1-gothic-med">${bio}</div>
        </div>
        ${
          links.length
            ? `
        <div class="teams_modal_foot">
            <div class="teams_modal_foot_label | f-helvetica-now-bld">Links</div>
            <ul class="teams_modal_foot_list">
                ${links
                  .map(
                    (s) => `<li class="teams_modal_foot_list_i">
                    <a href="${s.url}" target="_blank" rel="noopener noferrer" class="teams_modal_foot_list_link | f-helvetica-now-bld">${this.escapeHTML(s.label || s.url)}</a>
                </li>`
                  )
                  .join('')}
            </ul>
        </div>`
            : ''
        }
      </div>
    `
    }
  }

  escapeHTML(s) {
    return String(s).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;')
  }
}